/**
 * CREATIVE DETONATOR - SESSIONS MODULE
 * Handles Sessions History page functionality
 *
 * Features:
 * - Session timeline rendering
 * - Session detail expansion
 * - Reopen/continue session
 * - Session export
 * - Session analytics
 *
 * Uses IdeaCardRenderer for consistent card rendering
 * Uses DataMapper for centralized field access
 * v5 - Use unified empty-state.js module
 * FRONTEND-21: XSS Prevention via escapeHtml
 */
import { renderPresetEmptyState } from '../../../js/shared/empty-state.js';
import { escapeHtml, escapeAttr } from '../../../js/core/sanitize.js';

const SessionsModule = (function() {
  'use strict';

  // DashboardCore refs - populated after core is ready
  let Toast, Modal, Sounds, Sessions, Brands, Ideas, Utils, Storage;

  // ---------------------------------------------------------------------------
  // Configuration
  // ---------------------------------------------------------------------------
  const CONFIG = {
    filters: {
      brand: '',
      status: '',
      dateRange: ''
    },
    sortBy: 'newest',
    groupBy: 'date', // 'date' or 'brand'
    expandedSessions: new Set()
  };

  // ---------------------------------------------------------------------------
  // DOM Elements
  // ---------------------------------------------------------------------------
  let elements = {};

  // ---------------------------------------------------------------------------
  // Unified Renderer and Data Mapper Access
  // ---------------------------------------------------------------------------

  function getRenderer() {
    return window.IdeaCardRenderer || null;
  }

  function getMapper() {
    return window.DataMapper || null;
  }

  function getCategoryColor(triggerCode) {
    const renderer = getRenderer();
    if (renderer) return renderer.getCategoryColor(triggerCode);
    // Fallback - use full CategoryColors from window
    const CategoryColors = window.CategoryColors || {
      'A': '#FF00FF', 'B': '#FF6600', 'C': '#00FFFF', 'D': '#00FF00',
      'E': '#9900FF', 'F': '#FFFF00', 'G': '#0066FF', 'H': '#FF0000',
      'I': '#FF6B9D', 'J': '#4ECDC4', 'K': '#9B59B6', 'L': '#E74C3C',
      'M': '#2ECC71', 'N': '#1ABC9C', 'O': '#F39C12'
    };
    const cat = triggerCode?.charAt(0).toUpperCase() || 'A';
    return CategoryColors[cat] || '#888888';
  }

  // Accessor helpers using data-mapper or fallback
  function getImageUrl(idea) {
    const mapper = getMapper();
    if (mapper) return mapper.getImageUrl(idea);
    return idea?.imageUrl || idea?.image_url || null;
  }

  function isSaved(idea) {
    const mapper = getMapper();
    if (mapper) return mapper.isSaved(idea);
    return idea?.saved || idea?.is_saved || false;
  }

  function getTriggerCode(idea) {
    const mapper = getMapper();
    if (mapper) return mapper.getTriggerCode(idea);
    return idea?.triggerCode || idea?.trigger_code || idea?.trigger || 'A1';
  }

  // ---------------------------------------------------------------------------
  // Rendering
  // ---------------------------------------------------------------------------
  function renderSessionsTimeline() {
    if (!elements.timeline) return;

    const sessions = applyFilters();

    if (sessions.length === 0) {
      elements.timeline.innerHTML = renderPresetEmptyState('sessions');
      return;
    }

    // Group sessions by date
    const grouped = groupSessionsByDate(sessions);

    elements.timeline.innerHTML = Object.entries(grouped)
      .map(([date, dateSessions]) => renderDateGroup(date, dateSessions))
      .join('');

    // Bind events
    bindSessionEvents();
    updateStats(sessions);
  }

  function groupSessionsByDate(sessions) {
    const groups = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    sessions.forEach(session => {
      const sessionDate = new Date(session.createdAt);
      sessionDate.setHours(0, 0, 0, 0);

      let dateKey;
      if (sessionDate.getTime() === today.getTime()) {
        dateKey = 'Today';
      } else if (sessionDate.getTime() === yesterday.getTime()) {
        dateKey = 'Yesterday';
      } else {
        dateKey = sessionDate.toLocaleDateString('en-US', {
          weekday: 'long',
          month: 'long',
          day: 'numeric'
        });
      }

      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(session);
    });

    return groups;
  }

  function renderDateGroup(date, sessions) {
    return `
      <div class="timeline-date-group">
        <div class="timeline-date-header">
          <span class="timeline-date-marker"></span>
          <span class="timeline-date-label">${date}</span>
          <span class="timeline-date-count">${sessions.length} session${sessions.length !== 1 ? 's' : ''}</span>
        </div>
        <div class="timeline-sessions">
          ${sessions.map(session => renderSessionCard(session)).join('')}
        </div>
      </div>
    `;
  }

  // FRONTEND-21: XSS Prevention - all user data escaped
  function renderSessionCard(session) {
    const isExpanded = CONFIG.expandedSessions.has(session.id);
    const brand = session.brandId ? Brands.getById(session.brandId) : null;
    const triggers = session.triggers || [];
    const ideas = session.ideas || [];

    // Calculate stats using accessor functions for consistency
    const ideasGenerated = session.stats?.ideasGenerated || ideas.length;
    const ideasSaved = session.stats?.ideasSaved || ideas.filter(i => isSaved(i)).length;
    const imagesGenerated = session.stats?.imagesGenerated || ideas.filter(i => getImageUrl(i)).length;

    const timeAgo = Utils.formatDate(session.createdAt);

    // Extract product and benefit from brief (already normalized by dbSessionToApp)
    // FRONTEND-21: Escape user-provided content
    const productName = escapeHtml(session.brief?.product || 'Creative Session');
    const benefit = session.brief?.benefit || '';
    const benefitDisplay = benefit ? escapeHtml(Utils.truncate(benefit, 100)) : '';

    // Brand display - show brand name if different from product
    const brandName = brand?.name ? escapeHtml(brand.name) : null;
    const showBrandRow = brandName && brandName.toLowerCase() !== productName.toLowerCase();

    return `
      <article class="session-card ${isExpanded ? 'expanded' : ''}" data-id="${escapeAttr(session.id)}">
        <div class="session-card-header">
          <div class="session-card-icon">
            <img src="/assets/icons/icon_0049_folder-icon.png" class="pixel-icon" alt="">
          </div>
          <div class="session-card-title">${productName}</div>
          <div class="session-card-time">${timeAgo}</div>
        </div>

        ${(showBrandRow || benefitDisplay) ? `
        <div class="session-card-context">
          ${showBrandRow ? `
            <div class="session-context-row">
              <span class="session-context-label">BRAND:</span>
              <span class="session-context-value">${brandName}</span>
            </div>
          ` : ''}
          ${benefitDisplay ? `
            <div class="session-context-row">
              <span class="session-context-label">BENEFIT:</span>
              <span class="session-context-value">${benefitDisplay}</span>
            </div>
          ` : ''}
        </div>
        ` : ''}

        <div class="session-card-body">

          <div class="session-card-row">
            <div class="session-triggers-inline">
              ${triggers.slice(0, 5).map(trigger => {
                // Use getCategoryColor helper instead of TRIGGER_CATEGORIES
                const color = getCategoryColor(trigger);
                return `<span class="trigger-tag" style="background: ${escapeAttr(color)}; color: #000;">${escapeHtml(trigger)}</span>`;
              }).join('')}
              ${triggers.length > 5 ? `<span class="trigger-tag more">+${triggers.length - 5}</span>` : ''}
            </div>
            <div class="session-stats-inline">
              <span class="stat-inline"><img src="/assets/icons/icon_0045_idea-light-bulb.png" class="pixel-icon xs" alt="">${ideasGenerated}</span>
              <span class="stat-inline"><img src="/assets/icons/icon_0026_star-filled.png" class="pixel-icon xs" alt="">${ideasSaved}</span>
              ${imagesGenerated > 0 ? `<span class="stat-inline"><img src="/assets/icons/icon_0046_picture-frame.png" class="pixel-icon xs" alt="">${imagesGenerated}</span>` : ''}
            </div>
          </div>
        </div>

        <div class="session-card-actions">
          <button class="btn btn-sm btn-outline btn-expand">
            <img src="/assets/icons/icon_0007_eye.png" class="pixel-icon xs" alt=""> View Ideas
          </button>
          <button class="btn btn-sm btn-primary btn-reopen">
            <img src="/assets/icons/icon_0004_spark-fire.png" class="pixel-icon xs invert" alt=""> ${session.status === 'active' ? 'CONTINUE' : 'REOPEN'}
          </button>
          <div class="export-dropdown session-export-dropdown" data-id="${escapeAttr(session.id)}">
            <button class="btn btn-sm btn-outline btn-export-toggle">
              <img src="/assets/icons/icon_0031_open-box.png" class="pixel-icon xs" alt=""> <span class="btn-text">EXPORT</span> <span class="dropdown-arrow">▼</span>
            </button>
            <div class="export-menu">
              <button class="export-option" data-format="pdf">
                <img src="/assets/icons/icon_0046_picture-frame.png" class="pixel-icon xs" alt="">
                <span><strong>PDF</strong> <small>with images</small></span>
              </button>
              <button class="export-option" data-format="txt">
                <img src="/assets/icons/icon_0008_document.png" class="pixel-icon xs" alt="">
                <span><strong>TXT</strong> <small>text only</small></span>
              </button>
            </div>
          </div>
          <button class="btn btn-sm btn-outline btn-delete-session" data-id="${escapeAttr(session.id)}">
            <img src="/assets/icons/icon_0040_trash-bin.png" class="pixel-icon xs" alt=""> Delete
          </button>
        </div>

        ${isExpanded ? renderSessionDetails(session, ideas) : ''}
      </article>
    `;
  }

  // FRONTEND-21: XSS Prevention in session details
  function renderSessionDetails(session, ideas) {
    // Use unified renderer if available
    const renderer = getRenderer();

    return `
      <div class="session-details">
        ${ideas.length > 0 ? `
          <div class="session-detail-section">
            <h4><img src="/assets/icons/icon_0045_idea-light-bulb.png" class="pixel-icon xs" alt=""> Ideas (${ideas.length})</h4>
            <div class="session-ideas-preview" data-session-id="${escapeAttr(session.id)}">
              ${renderer && renderer.renderMiniIdeaCard
                ? ideas.map((idea, index) => renderer.renderMiniIdeaCard(idea, index)).join('')
                : ideas.map((idea, index) => {
                    // Use accessor functions for consistent field access
                    const trigger = getTriggerCode(idea);
                    const color = getCategoryColor(trigger);
                    const imageUrl = getImageUrl(idea);
                    const saved = isSaved(idea);
                    const headline = escapeHtml(Utils.truncate(idea.headline || '', 50));
                    return `
                    <div class="mini-idea-card ${saved ? 'saved' : ''}" data-idea-index="${index}">
                      ${imageUrl ? `
                        <div class="mini-idea-image">
                          <img src="${escapeAttr(imageUrl)}" alt="">
                        </div>
                      ` : ''}
                      <div class="mini-idea-content">
                        <span class="mini-idea-trigger" style="background: ${escapeAttr(color)}; color: #000;">${escapeHtml(trigger || '?')}</span>
                        <p class="mini-idea-headline">${headline}</p>
                      </div>
                      ${saved ? '<img src="/assets/icons/icon_0026_star-filled.png" class="pixel-icon xs saved-badge" alt="">' : ''}
                    </div>
                  `}).join('')
              }
            </div>
          </div>
        ` : ''}

        <div class="session-detail-section">
          <h4><img src="/assets/icons/icon_0023_info-circle.png" class="pixel-icon xs" alt=""> Details</h4>
          <div class="session-meta-grid">
            <div class="meta-item">
              <span class="meta-label">Started</span>
              <span class="meta-value">${new Date(session.createdAt).toLocaleString()}</span>
            </div>
            ${session.completedAt ? `
              <div class="meta-item">
                <span class="meta-label">Completed</span>
                <span class="meta-value">${new Date(session.completedAt).toLocaleString()}</span>
              </div>
            ` : ''}
            <div class="meta-item">
              <span class="meta-label">Triggers</span>
              <span class="meta-value">${session.triggers?.length || 0} used</span>
            </div>
            <div class="meta-item">
              <span class="meta-label">ID</span>
              <span class="meta-value mono">${escapeHtml(session.id.substring(0, 8))}...</span>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  function bindSessionEvents() {
    elements.timeline?.querySelectorAll('.session-card').forEach(card => {
      const sessionId = card.dataset.id;

      card.querySelector('.btn-expand')?.addEventListener('click', (e) => {
        e.stopPropagation();
        toggleSessionExpand(sessionId);
      });

      card.querySelector('.btn-reopen')?.addEventListener('click', (e) => {
        e.stopPropagation();
        reopenSession(sessionId);
      });

      // Export dropdown handlers
      const exportDropdown = card.querySelector('.session-export-dropdown');
      if (exportDropdown) {
        const toggleBtn = exportDropdown.querySelector('.btn-export-toggle');
        const menu = exportDropdown.querySelector('.export-menu');

        toggleBtn?.addEventListener('click', (e) => {
          e.stopPropagation();
          // Close other dropdowns first
          document.querySelectorAll('.session-export-dropdown.open').forEach(d => {
            if (d !== exportDropdown) d.classList.remove('open');
          });
          exportDropdown.classList.toggle('open');
        });

        // PDF export
        menu?.querySelector('[data-format="pdf"]')?.addEventListener('click', (e) => {
          e.stopPropagation();
          exportDropdown.classList.remove('open');
          exportSessionPdf(sessionId);
        });

        // TXT export
        menu?.querySelector('[data-format="txt"]')?.addEventListener('click', (e) => {
          e.stopPropagation();
          exportDropdown.classList.remove('open');
          exportSession(sessionId);
        });
      }

      card.querySelector('.btn-delete-session')?.addEventListener('click', (e) => {
        e.stopPropagation();
        deleteSession(sessionId);
      });
    });

    // Close dropdowns when clicking outside
    document.addEventListener('click', () => {
      document.querySelectorAll('.session-export-dropdown.open').forEach(d => d.classList.remove('open'));
    });
  }

  // ---------------------------------------------------------------------------
  // Filtering
  // ---------------------------------------------------------------------------
  function applyFilters() {
    let sessions = [...Sessions.items];

    // Brand filter
    if (CONFIG.filters.brand) {
      sessions = sessions.filter(s => s.brandId === CONFIG.filters.brand);
    }

    // Status filter
    if (CONFIG.filters.status) {
      sessions = sessions.filter(s => s.status === CONFIG.filters.status);
    }

    // Date range filter
    if (CONFIG.filters.dateRange) {
      const now = new Date();
      let cutoff;

      switch (CONFIG.filters.dateRange) {
        case 'today':
          cutoff = new Date(now.setHours(0, 0, 0, 0));
          break;
        case 'week':
          cutoff = new Date(now.setDate(now.getDate() - 7));
          break;
        case 'month':
          cutoff = new Date(now.setMonth(now.getMonth() - 1));
          break;
      }

      if (cutoff) {
        sessions = sessions.filter(s => new Date(s.createdAt) >= cutoff);
      }
    }

    // Sorting
    sessions.sort((a, b) => {
      switch (CONFIG.sortBy) {
        case 'oldest':
          return new Date(a.createdAt) - new Date(b.createdAt);
        case 'newest':
        default:
          return new Date(b.createdAt) - new Date(a.createdAt);
      }
    });

    return sessions;
  }

  // ---------------------------------------------------------------------------
  // Session Operations
  // ---------------------------------------------------------------------------
  function toggleSessionExpand(sessionId) {
    if (CONFIG.expandedSessions.has(sessionId)) {
      CONFIG.expandedSessions.delete(sessionId);
    } else {
      CONFIG.expandedSessions.add(sessionId);
    }
    renderSessionsTimeline();
    Sounds.play('click');
  }

  function reopenSession(sessionId) {
    const session = Sessions.items.find(s => s.id === sessionId);
    if (!session) return;

    // Store session data for the generator to pick up
    Storage.set('reopen_session', {
      id: session.id,
      brandId: session.brandId,
      brief: session.brief,
      triggers: session.triggers,
      ideas: session.ideas
    });

    Toast.success('Reopening session...', 'Loading');
    Sounds.play('coin');

    // Navigate to generator
    setTimeout(() => {
      window.location.href = '../../generator/index.html?reopen=' + sessionId;
    }, 500);
  }

  async function exportSession(sessionId) {
    const session = Sessions.items.find(s => s.id === sessionId);
    if (!session) return;

    // Find the export button and set loading state
    const card = document.querySelector(`.session-card[data-id="${sessionId}"]`);
    const exportBtn = card?.querySelector('.btn-export-toggle');
    const exportBtnText = exportBtn?.querySelector('.btn-text');
    const originalText = exportBtnText?.textContent || 'EXPORT';

    if (exportBtn) {
      exportBtn.disabled = true;
      exportBtn.classList.add('is-loading');
      if (exportBtnText) exportBtnText.textContent = 'EXPORTING...';
    }

    Toast.info('Preparing export...');

    try {
      // Get auth token for API calls
      const { getSessionToken } = await import('../../auth/js/client.js');
      const token = await getSessionToken();
      const authHeaders = token ? { 'Authorization': `Bearer ${token}` } : {};

      // Fetch full session data from API (includes meta with phases)
      const API_BASE = window.API_CONFIG_URL || 'https://api.creativedetonator.com';
      const fullSessionResponse = await fetch(`${API_BASE}/api/sessions/${sessionId}`, {
        headers: authHeaders
      });

      if (!fullSessionResponse.ok) {
        throw new Error('Failed to fetch session data');
      }

      const responseData = await fullSessionResponse.json();
      // API might return { data: session } or { session } or just session directly
      const fullSession = responseData.data || responseData.session || responseData;

      if (!fullSession || !fullSession.id) {
        throw new Error('Invalid session data received');
      }

      console.log('[Sessions] Export - fetched session:', fullSession.id);

      // Reconstruct session object for export API
      const meta = fullSession.meta || {};
      const triggers = fullSession.triggers || [];

      // Fetch full trigger data from API (for question/mechanism)
      let allTriggers = [];
      try {
        const triggersResponse = await fetch(`${API_BASE}/api/triggers`);
        if (triggersResponse.ok) {
          const triggersData = await triggersResponse.json();
          allTriggers = triggersData.triggers || triggersData || [];
        }
      } catch (err) {
        console.warn('[Sessions] Could not fetch triggers:', err);
      }

      // Build trigger objects from codes with full data
      const triggerObjects = triggers.map(t => {
        const code = typeof t === 'string' ? t : t.code;
        const triggerData = allTriggers.find(tr => tr.code === code);
        return {
          code,
          name: triggerData?.name || t?.name || '',
          question: triggerData?.question || t?.question || '',
          mechanism: triggerData?.mechanism || t?.mechanism || ''
        };
      });

      // Build session object that matches what export API expects
      const exportSession = {
        version: meta.version || fullSession.prompt_version || 'v5',
        timestamp: fullSession.created_at,
        brief: typeof fullSession.brief === 'object' ? {
          ...fullSession.brief,
          formats: fullSession.brief.formats || fullSession.formats || []
        } : {
          product: fullSession.brief || '',
          benefit: '',
          formats: fullSession.formats || []
        },
        // TXT export expects 'triggers', PDF expects 'selectedTriggers' - provide both
        triggers: triggerObjects,
        selectedTriggers: triggerObjects,
        // Phase data from meta
        phase1: meta.phase1 || null,
        phase2a: meta.phase2a || null,
        phase2b: meta.phase2b || null,
        phase3: meta.phase3 || null,
        // Stats for TXT export footer
        stats: meta.stats || {
          fragmentsGenerated: meta.phase1?.fragmentsGenerated || 0,
          fragmentsInterpreted: meta.phase2a?.parsedResponse?.length || 0,
          fragmentsSelected: meta.phase2b?.parsedResponse?.selected?.length || 0,
          formatsGenerated: (fullSession.formats || []).length,
          ideasGenerated: (fullSession.ideas || []).length,
          totalDuration_ms: (meta.phase1?.duration_ms || 0) + (meta.phase2a?.duration_ms || 0) +
                           (meta.phase2b?.duration_ms || 0) + (meta.phase3?.duration_ms || 0),
          totalTokens: (meta.phase1?.tokens || 0) + (meta.phase2a?.tokens || 0) +
                       (meta.phase2b?.tokens || 0) + (meta.phase3?.tokens || 0),
          phases: 4
        },
        // Ideas count for TXT export
        ideasCount: (fullSession.ideas || []).length,
        // Results - ALWAYS use fresh ideas from DB (not meta.results)
        // Transform DB snake_case to formatter camelCase
        results: (fullSession.ideas || []).reduce((acc, idea) => {
          const code = idea.trigger_code || idea.triggerCode || '';
          let group = acc.find(g => g.triggerCode === code);
          if (!group) {
            const triggerData = window.DataStore?.getTriggerByCode(code);
            group = { triggerCode: code, triggerName: triggerData?.name || '', ideas: [] };
            acc.push(group);
          }
          // Map DB fields to formatter expected fields
          group.ideas.push({
            ...idea,
            // Core fields
            title: idea.meta?.title || idea.headline || '',
            idea: idea.meta?.idea || '',
            visual: idea.visual || '',
            text: idea.visual || '', // Alias for formatter
            format: idea.format || 'print',
            // Lineage fields (snake_case -> camelCase)
            sourceFragment: idea.source_fragment || idea.sourceFragment || '',
            sourceTruth: idea.source_truth || idea.sourceTruth || '',
            // Explanation
            whyItWorks: idea.explanation || '',
            explanation: idea.explanation || ''
          });
          return acc;
        }, [])
      };

      // Call backend TXT export API
      const exportResponse = await fetch(`${API_BASE}/api/export-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders
        },
        body: JSON.stringify({ session: exportSession })
      });

      if (!exportResponse.ok) {
        throw new Error('Export failed');
      }

      // Get the TXT content as blob and download
      const blob = await exportResponse.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const productName = (fullSession.brief?.product || fullSession.brief || 'session')
        .toString()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .substring(0, 30);
      a.download = `creative-session-${productName}-${Date.now()}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      Toast.success('Session exported!');
      Sounds.play('success');

    } catch (error) {
      console.error('[Sessions] Export error:', error);
      Toast.error('Export failed: ' + error.message);
      Sounds.play('error');
    } finally {
      // Reset export button state
      if (exportBtn) {
        exportBtn.disabled = false;
        exportBtn.classList.remove('is-loading');
        if (exportBtnText) exportBtnText.textContent = originalText;
      }
    }
  }

  /**
   * Export session as PDF with images
   */
  async function exportSessionPdf(sessionId) {
    const session = Sessions.items.find(s => s.id === sessionId);
    if (!session) return;

    // Find the export button and set loading state
    const card = document.querySelector(`.session-card[data-id="${sessionId}"]`);
    const exportBtn = card?.querySelector('.btn-export-toggle');
    const exportBtnText = exportBtn?.querySelector('.btn-text');
    const originalText = exportBtnText?.textContent || 'EXPORT';

    if (exportBtn) {
      exportBtn.disabled = true;
      exportBtn.classList.add('is-loading');
      if (exportBtnText) exportBtnText.textContent = 'EXPORTING...';
    }

    Toast.info('Generating PDF...');

    try {
      // Get auth token for API calls
      const { getSessionToken } = await import('../../auth/js/client.js');
      const token = await getSessionToken();
      const authHeaders = token ? { 'Authorization': `Bearer ${token}` } : {};

      // Fetch full session data from API (includes meta with phases)
      const API_BASE = window.API_CONFIG_URL || 'https://api.creativedetonator.com';
      const fullSessionResponse = await fetch(`${API_BASE}/api/sessions/${sessionId}`, {
        headers: authHeaders
      });

      if (!fullSessionResponse.ok) {
        throw new Error('Failed to fetch session data');
      }

      const responseData = await fullSessionResponse.json();
      const fullSession = responseData.data || responseData.session || responseData;

      if (!fullSession || !fullSession.id) {
        throw new Error('Invalid session data received');
      }

      // Reconstruct session object for export API
      const meta = fullSession.meta || {};
      const triggers = fullSession.triggers || [];

      // Fetch full trigger data from API (for question/mechanism)
      let allTriggers = [];
      try {
        const triggersResponse = await fetch(`${API_BASE}/api/triggers`);
        if (triggersResponse.ok) {
          const triggersData = await triggersResponse.json();
          allTriggers = triggersData.triggers || triggersData || [];
        }
      } catch (err) {
        console.warn('[Sessions PDF] Could not fetch triggers:', err);
      }

      // Build trigger objects from codes with full data
      const triggerObjects = triggers.map(t => {
        const code = typeof t === 'string' ? t : t.code;
        const triggerData = allTriggers.find(tr => tr.code === code);
        return {
          code,
          name: triggerData?.name || t?.name || '',
          question: triggerData?.question || t?.question || '',
          mechanism: triggerData?.mechanism || t?.mechanism || ''
        };
      });

      // Build results with images - ALWAYS use fullSession.ideas (has current image URLs)
      // DO NOT use meta.results - it was saved before images were generated!
      const ideas = fullSession.ideas || [];
      const resultsWithImages = ideas.reduce((acc, idea) => {
        const code = idea.trigger_code || idea.triggerCode || '';
        let group = acc.find(g => g.triggerCode === code);
        if (!group) {
          const triggerData = window.DataStore?.getTriggerByCode(code);
          group = { triggerCode: code, triggerName: triggerData?.name || '', ideas: [] };
          acc.push(group);
        }
        // Map DB fields to PDF expected fields + include images
        group.ideas.push({
          ...idea,
          // Core fields
          title: idea.meta?.title || idea.headline || '',
          idea: idea.meta?.idea || '',
          visual: idea.visual || '',
          text: idea.visual || '',
          format: idea.format || 'print',
          // Lineage fields (snake_case -> camelCase)
          sourceFragment: idea.source_fragment || idea.sourceFragment || '',
          sourceTruth: idea.source_truth || idea.sourceTruth || '',
          // Explanation
          whyItWorks: idea.explanation || '',
          explanation: idea.explanation || '',
          // Images - ensure both formats
          image_url: idea.image_url || idea.imageUrl,
          imageUrl: idea.imageUrl || idea.image_url
        });
        return acc;
      }, []);

      // Debug: check how many ideas have images
      const ideasWithImages = ideas.filter(i => i.image_url || i.imageUrl).length;
      console.log('[Sessions PDF] Ideas from API:', ideas.length, 'with images:', ideasWithImages);
      if (ideas.length > 0) {
        console.log('[Sessions PDF] Sample idea image_url:', ideas[0].image_url);
      }

      // Build session object that matches what export API expects
      const exportSession = {
        version: meta.version || fullSession.prompt_version || 'v5',
        timestamp: fullSession.created_at,
        brief: typeof fullSession.brief === 'object' ? {
          ...fullSession.brief,
          formats: fullSession.brief.formats || fullSession.formats || []
        } : {
          product: fullSession.brief || '',
          benefit: '',
          formats: fullSession.formats || []
        },
        // Both triggers and selectedTriggers for compatibility
        triggers: triggerObjects,
        selectedTriggers: triggerObjects,
        phase1: meta.phase1 || null,
        phase2a: meta.phase2a || null,
        phase2b: meta.phase2b || null,
        phase3: meta.phase3 || null,
        // Stats for PDF
        stats: meta.stats || {
          fragmentsGenerated: meta.phase1?.fragmentsGenerated || 0,
          fragmentsInterpreted: meta.phase2a?.parsedResponse?.length || 0,
          fragmentsSelected: meta.phase2b?.parsedResponse?.selected?.length || 0,
          formatsGenerated: (fullSession.formats || []).length,
          ideasGenerated: ideas.length
        },
        results: resultsWithImages
      };

      // Call backend PDF export API
      const exportResponse = await fetch(`${API_BASE}/api/export-session-pdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders
        },
        body: JSON.stringify({
          session: exportSession,
          options: {
            includeFragments: true,
            includeImages: true,
            layout: 'cards'
          }
        })
      });

      if (!exportResponse.ok) {
        throw new Error('PDF generation failed');
      }

      // Get the PDF blob and download
      const blob = await exportResponse.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const productName = (fullSession.brief?.product || fullSession.brief || 'session')
        .toString()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .substring(0, 30);
      const dateStr = new Date().toISOString().split('T')[0];
      a.download = `creative-detonator-${productName}-${dateStr}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      Toast.success('PDF downloaded!');
      Sounds.play('success');

    } catch (error) {
      console.error('[Sessions] PDF Export error:', error);
      Toast.error('PDF export failed: ' + error.message);
      Sounds.play('error');
    } finally {
      // Reset export button state
      if (exportBtn) {
        exportBtn.disabled = false;
        exportBtn.classList.remove('is-loading');
        if (exportBtnText) exportBtnText.textContent = originalText;
      }
    }
  }

  function deleteSession(sessionId) {
    Modal.confirm({
      title: '🗑️ Delete Session',
      message: 'Are you sure you want to delete this session? This cannot be undone.',
      confirmText: 'Delete',
      confirmClass: 'btn-danger',
      onConfirm: () => {
        Sessions.items = Sessions.items.filter(s => s.id !== sessionId);
        // Phase 2: Don't cache sessions to localStorage - too large
        // Storage.set('sessions', Sessions.items);
        CONFIG.expandedSessions.delete(sessionId);
        Toast.success('Session deleted');
        renderSessionsTimeline();
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Stats
  // ---------------------------------------------------------------------------
  function updateStats(sessions) {
    const totalSessions = sessions.length;

    let totalIdeasGenerated = 0;
    let totalIdeasSaved = 0;
    let totalTriggersUsed = new Set();

    sessions.forEach(session => {
      const ideas = session.ideas || [];
      totalIdeasGenerated += session.stats?.ideasGenerated || ideas.length;
      // Use accessor function for consistency
      totalIdeasSaved += session.stats?.ideasSaved || ideas.filter(i => isSaved(i)).length;
      (session.triggers || []).forEach(t => totalTriggersUsed.add(t));
    });

    const statSessions = document.querySelector('.stat-total-sessions');
    const statGenerated = document.querySelector('.stat-ideas-generated');
    const statSaved = document.querySelector('.stat-ideas-saved');
    const statTriggers = document.querySelector('.stat-triggers-used');

    if (statSessions) statSessions.textContent = totalSessions;
    if (statGenerated) statGenerated.textContent = totalIdeasGenerated;
    if (statSaved) statSaved.textContent = totalIdeasSaved;
    if (statTriggers) statTriggers.textContent = totalTriggersUsed.size;
  }

  // ---------------------------------------------------------------------------
  // Utilities
  // ---------------------------------------------------------------------------
  function formatDuration(ms) {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    } else if (minutes > 0) {
      return `${minutes}m`;
    } else {
      return `${seconds}s`;
    }
  }

  // ---------------------------------------------------------------------------
  // DEEP LINKING - Handle URL parameters for navigation
  // ---------------------------------------------------------------------------

  /**
   * Handle URL parameters for deep linking to specific session
   * Supports: ?expand=<sessionId> - scroll to, expand, and highlight
   */
  function handleDeepLink() {
    const urlParams = new URLSearchParams(window.location.search);
    const expandId = urlParams.get('expand');

    if (!expandId) return;

    console.log('[Sessions] Deep link detected:', expandId);

    // Wait for initial render
    requestAnimationFrame(() => {
      setTimeout(() => {
        // 1. Find the session
        const session = Sessions.items.find(s => s.id === expandId);
        if (!session) {
          console.warn('[Sessions] Deep link target not found:', expandId);
          cleanDeepLinkUrl();
          return;
        }

        // 2. Expand if not already expanded
        if (!CONFIG.expandedSessions.has(expandId)) {
          CONFIG.expandedSessions.add(expandId);
          renderSessionsTimeline();
        }

        // 3. Wait for re-render, find and scroll
        requestAnimationFrame(() => {
          const card = document.querySelector(`.session-card[data-id="${expandId}"]`);

          if (card) {
            card.scrollIntoView({ behavior: 'smooth', block: 'start' });
            card.classList.add('deep-link-highlight');

            setTimeout(() => {
              card.classList.remove('deep-link-highlight');
            }, 3000);

            Sounds?.play('success');
          }

          cleanDeepLinkUrl();
        });
      }, 100);
    });
  }

  /**
   * Remove URL parameters without page reload
   */
  function cleanDeepLinkUrl() {
    const cleanPath = window.location.pathname;
    window.history.replaceState({}, document.title, cleanPath);
  }

  // ---------------------------------------------------------------------------
  // Initialize
  // ---------------------------------------------------------------------------
  async function init() {
    // Wait for DashboardCore and auth to be ready (with timeout)
    if (!window.DashboardCore || !window.DashboardCore.isDashboardReady()) {
      console.log('[Sessions] Waiting for dashboard to be ready...');

      if (window.DashboardCore?.waitForDashboard) {
        const ready = await window.DashboardCore.waitForDashboard(10000);
        if (!ready) {
          console.error('[Sessions] Dashboard timeout - proceeding anyway');
        }
      } else {
        await new Promise(resolve => {
          const timeout = setTimeout(() => {
            console.warn('[Sessions] Dashboard wait timeout');
            resolve();
          }, 10000);

          if (window.DashboardCore?.isDashboardReady()) {
            clearTimeout(timeout);
            resolve();
            return;
          }
          window.addEventListener('dashboard:ready', () => {
            clearTimeout(timeout);
            resolve();
          }, { once: true });
        });
      }
    }

    // Get refs from DashboardCore
    if (!window.DashboardCore) {
      console.error('[Sessions] DashboardCore not available');
      return;
    }
    ({ Toast, Modal, Sounds, Sessions, Brands, Ideas, Utils, Storage } = window.DashboardCore);

    console.log('[Sessions] Dashboard ready, loading data...');

    // Cache DOM elements
    elements = {
      timeline: document.getElementById('sessions-timeline'),
      filterBrand: document.getElementById('filter-brand'),
      filterStatus: document.getElementById('filter-status'),
      filterDate: document.getElementById('filter-date')
    };

    if (!elements.timeline) return;

    // Load data
    await Sessions.load();
    await Brands.load();
    await Ideas.load();

    // Populate brand filter
    // FRONTEND-21: Escape brand names in dropdown
    if (elements.filterBrand) {
      elements.filterBrand.innerHTML = `
        <option value="">All Brands</option>
        ${Brands.items.map(b => `
          <option value="${escapeAttr(b.id)}">${escapeHtml(b.icon || '🏷️')} ${escapeHtml(b.name)}</option>
        `).join('')}
      `;

      elements.filterBrand.addEventListener('change', (e) => {
        CONFIG.filters.brand = e.target.value;
        renderSessionsTimeline();
      });
    }

    // Status filter
    if (elements.filterStatus) {
      elements.filterStatus.addEventListener('change', (e) => {
        CONFIG.filters.status = e.target.value;
        renderSessionsTimeline();
      });
    }

    // Date filter
    if (elements.filterDate) {
      elements.filterDate.addEventListener('change', (e) => {
        CONFIG.filters.dateRange = e.target.value;
        renderSessionsTimeline();
      });
    }

    // Initial render
    renderSessionsTimeline();

    // Handle deep linking
    handleDeepLink();

    console.log('Sessions Module initialized');
  }

  // Auto-init if on sessions page
  if (document.querySelector('#sessions-timeline')) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------
  return {
    CONFIG,
    init,
    renderSessionsTimeline,
    toggleSessionExpand,
    reopenSession,
    exportSession,
    deleteSession
  };
})();

// Global reference
window.SessionsModule = SessionsModule;
